import React, { useMemo, useState } from "react";
import StatBlock from "./StatBlock";

const ALL_CURRENCIES = "ALL";

const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const emptyTotals = () => ({
  incomeReports: 0,
  incomeAmount: 0,
  invoiceTotal: 0,
  invoicePaidCount: 0,
  invoiceUnpaidCount: 0,
  invoicePaidAmount: 0,
  invoiceUnpaidAmount: 0,
});

const sumFinanceRows = (rows) =>
  rows.reduce(
    (acc, row) => ({
      incomeReports: acc.incomeReports + asNumber(row.income_reports),
      incomeAmount: acc.incomeAmount + asNumber(row.income_amount),
      invoiceTotal: acc.invoiceTotal + asNumber(row.invoice_total),
      invoicePaidCount: acc.invoicePaidCount + asNumber(row.invoice_paid_count),
      invoiceUnpaidCount: acc.invoiceUnpaidCount + asNumber(row.invoice_unpaid_count),
      invoicePaidAmount: acc.invoicePaidAmount + asNumber(row.invoice_paid_amount),
      invoiceUnpaidAmount: acc.invoiceUnpaidAmount + asNumber(row.invoice_unpaid_amount),
    }),
    emptyTotals()
  );

const FinanceTile = ({ financeByCurrency, providers, isLoading }) => {
  const currencies = useMemo(
    () => (Array.isArray(financeByCurrency) ? financeByCurrency.map((row) => row.currency) : []),
    [financeByCurrency]
  );

  const [selectedCurrency, setSelectedCurrency] = useState(ALL_CURRENCIES);

  const activeTotals = useMemo(() => {
    const rows = Array.isArray(financeByCurrency) ? financeByCurrency : [];
    if (selectedCurrency === ALL_CURRENCIES) {
      return sumFinanceRows(rows);
    }

    const match = rows.find((row) => row.currency === selectedCurrency);
    if (!match) {
      return emptyTotals();
    }

    return {
      incomeReports: asNumber(match.income_reports),
      incomeAmount: asNumber(match.income_amount),
      invoiceTotal: asNumber(match.invoice_total),
      invoicePaidCount: asNumber(match.invoice_paid_count),
      invoiceUnpaidCount: asNumber(match.invoice_unpaid_count),
      invoicePaidAmount: asNumber(match.invoice_paid_amount),
      invoiceUnpaidAmount: asNumber(match.invoice_unpaid_amount),
    };
  }, [financeByCurrency, selectedCurrency]);

  const displayCurrency =
    selectedCurrency === ALL_CURRENCIES ? undefined : selectedCurrency;

  return (
    <article className="dash-tile dash-tile--finance">
      <div className="dash-tile__head dash-tile__head--finance">
        <h2 className="dash-tile__title">Finance</h2>
        <div className="dash-finance-filter">
          <label htmlFor="financeCurrencyFilter" className="dash-finance-filter__label">
            Currency
          </label>
          <select
            id="financeCurrencyFilter"
            className="dash-finance-filter__select"
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            disabled={isLoading || currencies.length === 0}
          >
            <option value={ALL_CURRENCIES}>All currencies</option>
            {currencies.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="dash-tile__stats">
        <StatBlock label="Income reports" value={activeTotals.incomeReports} isLoading={isLoading} />
        <StatBlock label="Providers" value={providers} isLoading={isLoading} />
        <StatBlock label="Invoices" value={activeTotals.invoiceTotal} isLoading={isLoading} />
        <StatBlock label="Paid invoices" value={activeTotals.invoicePaidCount} isLoading={isLoading} />
        <StatBlock label="Unpaid invoices" value={activeTotals.invoiceUnpaidCount} isLoading={isLoading} />
        <StatBlock
          label={displayCurrency ? `Paid amount (${displayCurrency})` : "Paid amount (all)"}
          value={activeTotals.invoicePaidAmount}
          format="currency"
          currencyCode={displayCurrency}
          isLoading={isLoading}
        />
        <StatBlock
          label={displayCurrency ? `Unpaid amount (${displayCurrency})` : "Unpaid amount (all)"}
          value={activeTotals.invoiceUnpaidAmount}
          format="currency"
          currencyCode={displayCurrency}
          isLoading={isLoading}
        />
        <StatBlock
          label={displayCurrency ? `Revenue (${displayCurrency})` : "Revenue (all)"}
          value={activeTotals.incomeAmount}
          format="currency"
          currencyCode={displayCurrency}
          isLoading={isLoading}
        />
      </div>

      {selectedCurrency === ALL_CURRENCIES && currencies.length > 1 ? (
        <div className="dash-finance-breakdown">
          <p className="dash-finance-breakdown__title">Breakdown by currency</p>
          <div className="dash-finance-breakdown__grid">
            {(financeByCurrency || []).map((row) => (
              <button
                key={row.currency}
                type="button"
                className="dash-finance-breakdown__item"
                onClick={() => setSelectedCurrency(row.currency)}
              >
                <span className="dash-finance-breakdown__currency">{row.currency}</span>
                <span className="dash-finance-breakdown__line">
                  Paid {asNumber(row.invoice_paid_amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
                <span className="dash-finance-breakdown__line">
                  Unpaid {asNumber(row.invoice_unpaid_amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
                <span className="dash-finance-breakdown__line">
                  Revenue {asNumber(row.income_amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
};

export default FinanceTile;
